import React, { useEffect, useState } from 'react';
import type { CaptainMessage, OngoingOrderSnapshot } from '../types/events';
import {
  CaptainAvatar,
  CaptainChatIcon,
  CaptainExpandIcon,
  CaptainMessageCard,
  CaptainUpdatesSwitch,
  CaptainVoiceButton,
} from './CaptainParts';
import CaptainAssistance from './CaptainAssistance';

interface CaptainPopoverProps {
  message: CaptainMessage;
  restaurantName: string;
  voiceOn: boolean;
  onToggleVoice: () => void;
  autoOpenUpdates: boolean;
  onToggleAutoOpenUpdates: () => void;
  onExpand: () => void;
  onStartChat?: () => void;
  onDismiss: () => void;
  showVoiceTip: boolean;
  onEnableVoice: () => void;
  onDismissVoiceTip: () => void;
  chatEnabled?: boolean;
  assistanceAvailable?: boolean;
  order?: OngoingOrderSnapshot | null;
}

const CaptainPopover: React.FC<CaptainPopoverProps> = ({
  message,
  restaurantName,
  voiceOn,
  onToggleVoice,
  autoOpenUpdates,
  onToggleAutoOpenUpdates,
  onExpand,
  onStartChat,
  onDismiss,
  showVoiceTip,
  onEnableVoice,
  onDismissVoiceTip,
  chatEnabled = false,
  assistanceAvailable = false,
  order = null,
}) => {
  // canAssist is true when assistanceAvailable — even before an order exists (pre-order dine-in)
  const canAssist = assistanceAvailable;
  const canChat = chatEnabled && Boolean(onStartChat);
  const [view, setView] = useState<'update' | 'assist'>('update');
  const [assistSeen, setAssistSeen] = useState(false);

  useEffect(() => {
    setView('update');
  }, [message.id]);

  return (
    <div
      className={`vc-popover${view === 'assist' ? ' vc-popover--with-assistance' : ''}`}
      role="dialog"
      aria-label="Peppy AI"
    >
      <div className="vc-popover__header">
        <CaptainAvatar size={38} ring />
        <div className="vc-popover__header-info">
          <div className="vc-popover__title">Peppy AI</div>
          <div className="vc-popover__subtitle">{restaurantName}</div>
        </div>
        <button
          type="button"
          className="vc-popover__icon-btn vc-popover__icon-btn--round"
          onClick={onExpand}
          title="Open full view"
          aria-label="Open full view"
        >
          <CaptainExpandIcon />
        </button>
      </div>

      {canAssist && (
        <div className="vc-mode-switch vc-mode-switch--popover" role="tablist" aria-label="Captain views">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'update'}
            className={`vc-mode-switch__btn${view === 'update' ? ' vc-mode-switch__btn--active' : ''}`}
            onClick={() => setView('update')}
          >
            <CaptainChatIcon size={14} />
            Update
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'assist'}
            className={`vc-mode-switch__btn vc-mode-switch__btn--assist${view === 'assist' ? ' vc-mode-switch__btn--active' : ''}`}
            onClick={() => { setView('assist'); setAssistSeen(true); }}
          >
            <i className="fa fa-bell" aria-hidden />
            Assistance
            {!assistSeen && view !== 'assist' && (
              <span className="vc-mode-switch__nudge" aria-hidden />
            )}
          </button>
        </div>
      )}

      <div className="vc-popover__body">
        {view === 'update' ? (
          <>
            <div className="vc-popover__updates">
              <CaptainMessageCard
                key={message.id}
                type={message.type}
                title={message.title}
                text={message.text}
                time={message.time}
                eta={message.eta}
                animate={message.animate}
              />
            </div>

            {showVoiceTip && (
              <div className="vc-voice-tip">
                <span>🔔</span>
                <span className="vc-voice-tip__text">
                  Enable voice updates? Audio is off by default — tap Yes to hear Peppy AI.
                </span>
                <button type="button" className="vc-voice-tip__yes" onClick={onEnableVoice}>
                  Yes
                </button>
                <button type="button" className="vc-voice-tip__no" onClick={onDismissVoiceTip}>
                  No
                </button>
              </div>
            )}
          </>
        ) : (
          canAssist && (
            <div className="vc-popover__section vc-popover__section--assist">
              <div className="vc-popover__section-head">
                <span className="vc-popover__section-icon" aria-hidden>
                  🔔
                </span>
                <div>
                  <h3 className="vc-popover__section-title">Need assistance?</h3>
                  <p className="vc-popover__section-sub">
                    Tap a shortcut — staff is notified instantly.
                  </p>
                </div>
              </div>
              <CaptainAssistance order={order ?? undefined} variant="popover" />
            </div>
          )
        )}
      </div>

      {view === 'update' && canChat && (
        <div className="vc-popover__actions">
          <button type="button" className="vc-popover__start-chat" onClick={onStartChat}>
            <CaptainChatIcon size={17} />
            Start Chat
          </button>
        </div>
      )}


      <div className="vc-popover__footer">
        <CaptainUpdatesSwitch on={autoOpenUpdates} onToggle={onToggleAutoOpenUpdates} compact />
        <CaptainVoiceButton on={voiceOn} onToggle={onToggleVoice} />
        <div className="vc-popover__spacer" />
        <button
          type="button"
          className="vc-popover__icon-btn vc-popover__icon-btn--round"
          onClick={onDismiss}
          title="Close"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CaptainPopover;
